import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePdf, FileCsv, ArrowsDownUp, HandCoins } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { PF } from './pfApi';
import { DateField } from '@/components/ui/date-field';

const fmtEurPt = (v) => `${(v ?? 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const iso = (d) => d.toISOString().slice(0, 10);
const fmtDatePt = (s) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };

function periodRange(mode, customStart, customEnd) {
  const now = new Date();
  if (mode === 'month') return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(now) };
  if (mode === 'prev_month') return { start: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (mode === 'year') return { start: iso(new Date(now.getFullYear(), 0, 1)), end: iso(new Date(now.getFullYear(), 11, 31)) };
  return { start: customStart || iso(now), end: customEnd || iso(now) };
}

const PERIODS = [['month', 'Mês atual'], ['prev_month', 'Mês anterior'], ['year', 'Ano'], ['range', 'Intervalo']];
const STATUS_LABEL = { por_pedir: 'Por pedir', pedido: 'Pedido', recebido_parcial: 'Recebido parcial', recebido: 'Recebido' };

export default function PfAccountStatement({ accounts, categories, initialAccountId }) {
  const [accountId, setAccountId] = useState(initialAccountId || accounts[0]?.id || '');
  const [periodMode, setPeriodMode] = useState('month');
  const [customStart, setCustomStart] = useState(iso(new Date()));
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [type, setType] = useState('all');
  const [categoryId, setCategoryId] = useState('all');
  const [search, setSearch] = useState('');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (initialAccountId) setAccountId(initialAccountId); }, [initialAccountId]);

  const { start, end } = periodRange(periodMode, customStart, customEnd);

  const buildParams = useCallback((p) => ({
    start, end, type: type === 'all' ? '' : type, category_id: categoryId === 'all' ? '' : categoryId, search, order, page: p, page_size: 30,
  }), [start, end, type, categoryId, search, order]);

  const fetchPage = useCallback(async (p, append) => {
    if (!accountId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${PF}/accounts/${accountId}/statement`, { params: buildParams(p) });
      setData(data);
      setRows(r => append ? [...r, ...data.movements] : data.movements);
    } catch { toast.error('Erro ao carregar extrato'); }
    setLoading(false);
  }, [accountId, buildParams]);

  useEffect(() => { setPage(1); fetchPage(1, false); }, [accountId, periodMode, customStart, customEnd, type, categoryId, search, order]); // eslint-disable-line

  const loadMore = () => { const next = page + 1; setPage(next); fetchPage(next, true); };

  const exportUrl = (kind) => {
    const p = buildParams(1);
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([k]) => k !== 'page' && k !== 'page_size'))).toString();
    return `${PF}/accounts/${accountId}/statement/${kind}?${qs}`;
  };

  const account = accounts.find(a => a.id === accountId);
  const hasMore = data && rows.length < data.total_count;

  return (
    <div data-testid="pf-statement-tab">
      <div className="flex flex-wrap gap-2 mb-3">
        <Select value={accountId} onValueChange={setAccountId}>
          <SelectTrigger className="rounded-full border-brand-border h-8 text-xs w-auto" data-testid="pf-statement-account-select"><SelectValue placeholder="Conta" /></SelectTrigger>
          <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
        {PERIODS.map(([v, l]) => (
          <button key={v} onClick={() => setPeriodMode(v)} className={`text-xs px-3 py-1.5 rounded-full border ${periodMode === v ? 'bg-brand-text text-white border-brand-text' : 'bg-white text-brand-muted border-brand-border'}`} data-testid={`pf-statement-period-${v}`}>{l}</button>
        ))}
        {periodMode === 'range' && (
          <>
            <DateField value={customStart} onChange={e => setCustomStart(e.target.value)} className="rounded-full border border-brand-border h-8 text-xs px-3" data-testid="pf-statement-range-start" />
            <DateField value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="rounded-full border border-brand-border h-8 text-xs px-3" data-testid="pf-statement-range-end" />
          </>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="rounded-full border-brand-border h-8 text-xs w-auto" data-testid="pf-statement-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos</SelectItem><SelectItem value="despesa">Despesas</SelectItem><SelectItem value="receita">Recebimentos</SelectItem></SelectContent>
        </Select>
        <Select value={categoryId} onValueChange={setCategoryId}>
          <SelectTrigger className="rounded-full border-brand-border h-8 text-xs w-auto" data-testid="pf-statement-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todas categorias</SelectItem>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar descrição…" className="rounded-full border-brand-border h-8 text-xs w-40" data-testid="pf-statement-search" />
        <button onClick={() => setOrder(o => o === 'desc' ? 'asc' : 'desc')} className="text-xs px-3 py-1.5 rounded-full border border-brand-border bg-white text-brand-muted flex items-center gap-1" data-testid="pf-statement-order-toggle">
          <ArrowsDownUp size={13} /> {order === 'desc' ? 'Mais recente primeiro' : 'Mais antigo primeiro'}
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="bg-brand-green/5 border border-brand-green/20 rounded-2xl p-3" data-testid="pf-statement-total-receitas">
            <p className="text-[11px] text-brand-muted">Recebimentos</p><p className="text-base font-heading font-semibold text-brand-green">{fmtEurPt(data.totals.receitas)}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-2xl p-3" data-testid="pf-statement-total-despesas">
            <p className="text-[11px] text-brand-muted">Despesas</p><p className="text-base font-heading font-semibold text-red-600">{fmtEurPt(data.totals.despesas)}</p>
          </div>
          {data.totals.poupanca !== undefined && data.totals.poupanca !== 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3" data-testid="pf-statement-total-poupanca">
              <p className="text-[11px] text-brand-muted">Poupança</p><p className="text-base font-heading font-semibold text-blue-700">{fmtEurPt(data.totals.poupanca)}</p>
            </div>
          )}
          <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="pf-statement-total-saldo">
            <p className="text-[11px] text-brand-muted">Saldo do período</p><p className="text-base font-heading font-semibold text-brand-text">{fmtEurPt(data.totals.saldo)}</p>
          </div>
          {data.opening_balance !== null && (
            <div className="bg-white border border-brand-border rounded-2xl p-3" data-testid="pf-statement-saldo-inicial-final">
              <p className="text-[11px] text-brand-muted">Saldo inicial → final</p><p className="text-base font-heading font-semibold text-brand-text">{fmtEurPt(data.opening_balance)} → {fmtEurPt(data.closing_balance)}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <a href={exportUrl('pdf')} target="_blank" rel="noreferrer" className="text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="pf-statement-export-pdf"><FilePdf size={13} /> Exportar PDF</a>
        <a href={exportUrl('csv')} className="text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5 flex items-center gap-1.5" data-testid="pf-statement-export-csv"><FileCsv size={13} /> Exportar CSV</a>
      </div>

      <div className="space-y-1.5">
        {rows.map(m => (
          <div key={m.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`pf-statement-row-${m.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-brand-text truncate flex items-center gap-1.5">
                {m.note || '(sem descrição)'}
                {m.is_shared && <span className="text-[9px] font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><HandCoins size={9} /> {STATUS_LABEL[m.reimbursement_status] || 'Partilhada'}</span>}
              </p>
              <p className="text-[11px] text-brand-muted">{fmtDatePt(m.date)} · {m.category_name}{m.ambito && <span className="ml-1 text-brand-green">· {m.ambito}</span>}</p>
            </div>
            <div className="text-right shrink-0">
              <p className={`text-sm font-semibold ${m.type === 'despesa' ? 'text-red-600' : 'text-brand-green'}`}>{m.type === 'despesa' ? '-' : '+'}{fmtEurPt(m.amount)}</p>
              {m.balance_after !== null && <p className="text-[10px] text-brand-muted">saldo {fmtEurPt(m.balance_after)}</p>}
            </div>
          </div>
        ))}
        {!loading && rows.length === 0 && <p className="text-sm text-brand-placeholder text-center py-6" data-testid="pf-statement-empty">Sem movimentos para os filtros escolhidos.</p>}
      </div>
      {hasMore && <button onClick={loadMore} disabled={loading} className="w-full mt-3 text-xs text-brand-green py-2" data-testid="pf-statement-load-more">Carregar mais</button>}
    </div>
  );
}
